import {computed, DOCUMENT, effect, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {DialogService} from '../dialog/dialog.service';
import {AppStorageService} from '../app-storage/app-storage.service';
import {AppStorage} from '../../models/app-storage.model';
import {AppStorageIdEnum} from '../../enums/app-storage-id.enum';
import {AmagnoStorageKeyEnum} from '../../enums/amagno-storage-key.enum';
import {ThemeEnum} from '../../enums/theme.enum';
import {Icon} from '../../types/icons/icon.type';
import {ICONS} from '../../constants/icons/icons.constants';

@Injectable({ providedIn: 'root' })
export class ThemeService {
    private readonly dialogService = inject(DialogService);
    private readonly appStorageService = inject(AppStorageService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly window = inject(DOCUMENT).defaultView;
    private readonly storage: AppStorage;
    private readonly DARK_THEME_CSS_CLASS = 'dark-theme';
    private readonly COLOR_SCHEME_CSS_VARIABLE_NAME = '--color-scheme';
    private readonly isDarkModeEnabled: Signal<boolean>;
    private readonly currentTheme: WritableSignal<ThemeEnum>;
    private readonly currentThemeIcon: Signal<Icon>;

    public constructor() {
        this.storage = this.appStorageService.getStorage(AppStorageIdEnum.AMAGNO);
        let currentTheme: ThemeEnum | undefined = this.storage.get(AmagnoStorageKeyEnum.THEME);
        if (!currentTheme) {
            currentTheme = ThemeEnum.SYSTEM;
            this.storage.set(AmagnoStorageKeyEnum.THEME, currentTheme);
        }

        this.currentTheme = signal(currentTheme);
        this.currentThemeIcon = computed(() => {
            const currentTheme = this.currentTheme();
            return this.getThemeIconByTheme(currentTheme) ?? ICONS.LIGHT_DARK_MODE;
        });
        this.isDarkModeEnabled = computed(() => {
            const currentTheme = this.currentTheme();
            switch (currentTheme) {
                case ThemeEnum.SYSTEM:
                    return this.window?.matchMedia('(prefers-color-scheme: dark)').matches ?? false;
                case ThemeEnum.LIGHT:
                    return false;
                case ThemeEnum.DARK:
                default:
                    return true;
            }
        });

        effect(() => {
            const isDarkModeEnabled = this.isDarkModeEnabled();
            this.document.documentElement.style.setProperty(this.COLOR_SCHEME_CSS_VARIABLE_NAME, isDarkModeEnabled ? 'dark' : 'light');
            this.document.body.classList.toggle(this.DARK_THEME_CSS_CLASS, isDarkModeEnabled);
        });
    }

    public getCurrentThemeSignal(): Signal<ThemeEnum> {
        return this.currentTheme.asReadonly();
    }

    public getCurrentThemeIconSignal(): Signal<Icon> {
        return this.currentThemeIcon;
    }

    public getIsDarkModeSignal(): Signal<boolean> {
        return this.isDarkModeEnabled;
    }

    public async openSwitchThemeDialog(): Promise<void> {
        const selectedTheme = await this.dialogService.showThemeSelectionDialog(this.currentTheme());
        if (!selectedTheme || selectedTheme === this.currentTheme()) {
            return;
        }

        this.currentTheme.set(selectedTheme);
        this.storage.set(AmagnoStorageKeyEnum.THEME, selectedTheme);
    }

    public setCurrentTheme(theme: ThemeEnum): void {
        this.currentTheme.set(theme);
        this.storage.set(AmagnoStorageKeyEnum.THEME, theme);
    }

    private getThemeIconByTheme(theme: ThemeEnum): Icon | undefined {
        switch (theme) {
            case ThemeEnum.LIGHT:
                return ICONS.LIGHT_MODE;
            case ThemeEnum.DARK:
                return ICONS.DARK_MODE;
            case ThemeEnum.SYSTEM:
                return ICONS.LIGHT_DARK_MODE;
            default:
                return undefined;
        }
    }
}
